import { motion } from 'framer-motion'
import React, { useEffect } from 'react'
import { FieldValues, FormProvider, useFormContext } from 'react-hook-form'
import { Button, Tooltip } from '@demandlocal/components'
import { useModal } from '../../../components/useModal'
import { useAtom } from 'jotai'
import { createCampaignFormValuesAtom } from './CreateNewCampaignForm'
import CardTextInput from '../../../components/FormElements/CardTextInput'
import { ReactComponent as InventoryIcon } from '../../../images/campaign/inventory.svg'
import { ReactComponent as DollarSign } from '../../../images/icons/dollar_sign.svg'
import { ReactComponent as SpeedometerIcon } from '../../../images/icons/speedometer.svg'
import { ReactComponent as StockIcon } from '../../../images/icons/stock_number.svg'
import { ReactComponent as CalendarIcon } from '../../../images/icons/calendar_icon.svg'
import { useGetOverallStats } from '../../../api/dealership/useGetOverallStats'
import { useGetInventoryDataProviders } from '../../../api/admin/useGetInventoryDataProviders'
import { get, isUndefined, take, uniq } from 'lodash'
import VehicleSuggestionCard from '../../../components/FormElements/VehicleSuggestionCard'
import { useGetInventory } from '../../../api/dealership/inventory/useGetInventory'
import { formatNumber } from '../../../lib/utils/formatNumber'
import { useEditLinkCampaign } from '../../../api/dealership/campaigns/useEditLinkCampaign'
import { useNavigate } from 'react-router'
import CampaignProgressBar from '../../../components/CampaignProgressBars/CampaignProgressBar'
import classNames from 'classnames'

type Props = {
  step: number
  setStep: (step: number) => void
  inventory_campaign_type_id?: number
}

const CreateNewCampaignFilters = ({
  setStep,
  step,
  inventory_campaign_type_id = 1,
}: Props): JSX.Element => {
  const formContext = useFormContext()

  const [containerHeight, setContainerHeight] = React.useState(0)
  const { data: dealershipStats, isLoading } = useGetOverallStats()
  const { mutateAsync: saveLinkCampaign, isLoading: isSaving } = useEditLinkCampaign()
  const { data: dealershipDataProviders } = useGetInventoryDataProviders({
    filters: {
      inventory_data_provider_id:
        dealershipStats?.inventoryDataProviders?.inventory_data_provider_ids.map(
          (provider) => {
            return { value: provider }
          },
        ),
    },
  })
  const [formValues, setFormValues] = useAtom(createCampaignFormValuesAtom)
  const { handleSubmit, setValue, reset, watch } = formContext
  const { closeModal } = useModal()
  const formData = watch()
  const navigate = useNavigate()
  const { data: inventory } = useGetInventory({ pageSize: 99999 })
  const onSubmit = async (values: Partial<FieldValues>): Promise<void> => {
    const user_selected_vehicle_states: string[] = []
    if (values?.all_cars) {
      user_selected_vehicle_states.push('New', 'Used', 'Certified used')
    } else {
      if (values?.new_cars) user_selected_vehicle_states.push('New')
      if (values?.used_cars) user_selected_vehicle_states.push('Used', 'Certified used')
      if (
        isUndefined(values?.all_cars) &&
        isUndefined(values?.new_cars) &&
        isUndefined(values?.used_cars)
      )
        user_selected_vehicle_states.push('New', 'Used', 'Certified used')
    }

    setFormValues((prevFormValues) => {
      return {
        ...prevFormValues,
        [inventory_campaign_type_id === 1 ? 'inventory' : '']: {
          details: {
            inventory_data_providers: dealershipDataProviders?.data
              ?.map(
                (provider: Record<string, string | boolean | number>) =>
                  provider.inventory_data_provider_name,
              )
              .join(', '),
            inventory_data_provider_clients:
              dealershipStats?.inventoryDataProviders?.inventory_data_provider_client_ids.join(
                ',',
              ),
            allowed_vehicle_states: user_selected_vehicle_states.join(','),
          },
          filters: {
            allowed_makes: values?.make === 'custom' ? values?.allowed_makes : undefined,
            disallowed_makes:
              values?.make === 'custom' ? values?.disallowed_makes : undefined,
            allowed_models:
              values?.model === 'custom' ? values?.allowed_models : undefined,
            disallowed_models:
              values?.model === 'custom' ? values?.disallowed_models : undefined,
            lowest_allowed_price:
              values?.price === 'custom' ? values?.lowest_allowed_price : undefined,
            highest_allowed_price: values?.under_30k
              ? '30000'
              : values?.price === 'custom'
              ? values?.highest_allowed_price
              : undefined,
            no_price_only: values?.price === 'custom' ? values?.no_price_only : false,
            exclude_if_no_price:
              values?.price === 'custom' ? values?.exclude_if_no_price : false,
            lowest_allowed_mileage:
              values?.mileage === 'custom' ? values?.lowest_allowed_mileage : undefined,
            highest_allowed_mileage:
              values?.mileage === 'custom' ? values?.highest_allowed_mileage : undefined,
            allowed_stock_number_prefixes:
              values?.stock_number_prefixes === 'custom'
                ? values?.allowed_stock_number_prefixes
                : undefined,
            disallowed_stock_number_prefixes:
              values?.stock_number_prefixes === 'custom'
                ? values?.disallowed_stock_number_prefixes
                : undefined,
            minimum_age_days:
              values?.days_on_lot === 'custom' ? values?.minimum_age_days : undefined,
            maximum_age_days: values?.days_60_age
              ? '60'
              : values?.days_on_lot === 'custom'
              ? values?.maximum_age_days
              : undefined,
          },
        },
      }
    })

    if (formValues.campaign_id) {
      await saveLinkCampaign({
        data: {
          allowed_vehicle_states: user_selected_vehicle_states.join(','),
          allowed_makes: values?.make === 'custom' ? values?.allowed_makes : null,
          disallowed_makes: values?.make === 'custom' ? values?.disallowed_makes : null,
          allowed_models: values?.model === 'custom' ? values?.allowed_models : null,
          disallowed_models:
            values?.model === 'custom' ? values?.disallowed_models : null,
          lowest_allowed_price:
            values?.price === 'custom' ? values?.lowest_allowed_price : null,
          highest_allowed_price: values?.under_30k
            ? '30000'
            : values?.price === 'custom'
            ? values?.highest_allowed_price
            : null,
          no_price_only: values?.price === 'custom' ? values?.no_price_only : 0,
          exclude_if_no_price:
            values?.price === 'custom' ? values?.exclude_if_no_price : 0,
          lowest_allowed_mileage:
            values?.mileage === 'custom' ? values?.lowest_allowed_mileage : null,
          highest_allowed_mileage:
            values?.mileage === 'custom' ? values?.highest_allowed_mileage : null,
          allowed_stock_number_prefixes:
            values?.stock_number_prefixes === 'custom'
              ? values?.allowed_stock_number_prefixes
              : null,
          disallowed_stock_number_prefixes:
            values?.stock_number_prefixes === 'custom'
              ? values?.disallowed_stock_number_prefixes
              : null,
          minimum_age_days:
            values?.days_on_lot === 'custom' ? values?.minimum_age_days : null,
          maximum_age_days: values?.days_60_age
            ? '60'
            : values?.days_on_lot === 'custom'
            ? values?.maximum_age_days
            : null,
        },
        link_campaign_id: formValues.campaign_id,
      })
    }

    setStep(step + 1)
  }

  useEffect(() => {
    reset({
      make: 'any',
      model: 'any',
      price: 'any',
      mileage: 'any',
      stock_number_prefixes: 'any',
      days_on_lot: 'any',
    })
  }, [reset])

  useEffect(() => {
    switch (formValues.inventory?.details.allowed_vehicle_states) {
      case 'New':
        setValue('new_cars', true)
        break
      case 'Used,Certified used':
        setValue('used_cars', true)
        break
    }
    if (
      formValues.inventory?.filters?.allowed_makes ||
      formValues.inventory?.filters?.disallowed_makes
    ) {
      setValue('make', 'custom')
      setValue('allowed_makes', formValues.inventory.filters.allowed_makes)
      setValue('disallowed_makes', formValues.inventory.filters.disallowed_makes)
    }

    if (
      formValues.inventory?.filters?.allowed_models ||
      formValues.inventory?.filters?.disallowed_models
    ) {
      setValue('model', 'custom')
      setValue('allowed_models', formValues.inventory.filters.allowed_models)
      setValue('disallowed_models', formValues.inventory.filters.disallowed_models)
    }

    if (
      formValues.inventory?.filters?.highest_allowed_price ||
      formValues.inventory?.filters?.lowest_allowed_price
    ) {
      if (Number(formValues.inventory.filters.highest_allowed_price) === 30000)
        setValue('under_30k', true)
      setValue('price', 'custom')
      setValue(
        'highest_allowed_price',
        formValues.inventory.filters.highest_allowed_price,
      )
      setValue('lowest_allowed_price', formValues.inventory.filters.lowest_allowed_price)
    }

    if (
      formValues.inventory?.filters?.highest_allowed_mileage ||
      formValues.inventory?.filters?.lowest_allowed_mileage
    ) {
      setValue('mileage', 'custom')
      setValue(
        'highest_allowed_mileage',
        formValues.inventory.filters.highest_allowed_mileage,
      )
      setValue(
        'lowest_allowed_mileage',
        formValues.inventory.filters.lowest_allowed_mileage,
      )
    }

    if (
      formValues.inventory?.filters?.allowed_stock_number_prefixes ||
      formValues.inventory?.filters?.disallowed_stock_number_prefixes
    ) {
      setValue('stock_number_prefixes', 'custom')
      setValue(
        'allowed_stock_number_prefixes',
        formValues.inventory.filters.allowed_stock_number_prefixes,
      )
      setValue(
        'disallowed_stock_number_prefixes',
        formValues.inventory.filters.disallowed_stock_number_prefixes,
      )
    }

    if (
      formValues.inventory?.filters?.maximum_age_days ||
      formValues.inventory?.filters?.minimum_age_days
    ) {
      setValue('days_on_lot', 'custom')
      setValue('maximum_age_days', formValues.inventory.filters.maximum_age_days)
      setValue('minimum_age_days', formValues.inventory.filters.minimum_age_days)
    }
  }, [formValues, setValue])

  return (
    <FormProvider {...formContext}>
      <motion.form
        onSubmit={handleSubmit(onSubmit)}
        className={'flex flex-1 flex-col overflow-auto pb-20'}>
        <motion.div
          ref={(_ref) => {
            if (containerHeight === 0 && _ref?.clientHeight)
              setContainerHeight(_ref?.clientHeight ?? 0)
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={classNames('flex flex-1 flex-col pt-8 pb-8 overflow-auto', {
            'max-h-[auto]': containerHeight === 0,
            'max-h-full]': containerHeight !== 0,
          })}>
          {containerHeight > 0 && (
            <div className={'flex flex-col gap-5 px-12'}>
              <span className={'text-primary-persian-blue'}>Suggestions</span>
              <div
                className={
                  'flex flex-col gap-4 w-full border-2 border-primary-persian-blue/10 p-7 rounded-xl bg-primary-persian-blue/5'
                }>
                <div className={'flex flex-row gap-3 w-full'}>
                  <span className={'w-1/4'}>
                    <VehicleSuggestionCard
                      name={'all_cars'}
                      label={'All cars'}
                      value={dealershipStats?.totalVehicles || 0}
                    />
                  </span>
                  <span className={'w-1/4'}>
                    <VehicleSuggestionCard
                      name={'new_cars'}
                      label={'New cars'}
                      value={dealershipStats?.newVehicles || 0}
                    />
                  </span>
                  <span className={'w-1/4'}>
                    <VehicleSuggestionCard
                      name={'used_cars'}
                      label={'Used cars'}
                      value={dealershipStats?.usedVehicles || 0}
                    />
                  </span>
                  <span className={'w-1/4'}>
                    <VehicleSuggestionCard
                      name={'under_30k'}
                      label={'Under $30k'}
                      value={
                        Number(
                          inventory?.data?.filter(
                            (vehicle) => Number(vehicle.price) < 30000,
                          ).length,
                        ) || 0
                      }
                    />
                  </span>
                </div>
                {formData?.all_cars && (
                  <div className={'flex flex-col gap-2'}>
                    <span className={'font-bold text-xs'}>
                      All Cars ({dealershipStats?.totalVehicles})
                    </span>
                    <div className={'flex flex-row gap-3'}>
                      {take(
                        inventory?.data?.filter(
                          (vehicle) => vehicle.image_url.length > 0,
                        ),
                        10,
                      )?.map((vehicle) => {
                        return (
                          <Tooltip
                            key={vehicle?.vin_number}
                            label={`${vehicle?.year} ${vehicle?.raw_make} ${vehicle?.raw_model}`}>
                            <div className="w-20 h-20">
                              <img
                                className={'rounded-xl'}
                                src={get(vehicle?.image_url?.match(/https:\/\/.*?(?=\|\|\|\||\|https|,https|;https|$)/), '[0]', '')}
                              />
                            </div>
                          </Tooltip>
                        )
                      })}
                    </div>
                  </div>
                )}
                {formData?.new_cars && (
                  <div className={'flex flex-col gap-2'}>
                    <span className={'font-bold text-xs'}>
                      New Cars ({dealershipStats?.newVehicles})
                    </span>
                    <div className={'flex flex-row gap-3'}>
                      {take(
                        inventory?.data?.filter(
                          (vehicle) =>
                            vehicle.image_url.length > 0 &&
                            vehicle.vehicle_state_id === 1,
                        ),
                        10,
                      )?.map((vehicle) => {
                        return (
                          <Tooltip
                            key={vehicle?.vin_number}
                            label={`${vehicle?.year} ${vehicle?.raw_make} ${vehicle?.raw_model}`}>
                            <div className="w-20 h-20">
                              <img
                                className={'rounded-xl'}
                                src={get(vehicle?.image_url?.match(/https:\/\/.*?(?=\|\|\|\||\|https|,https|;https|$)/), '[0]', '')}
                              />
                            </div>
                          </Tooltip>
                        )
                      })}
                    </div>
                  </div>
                )}
                {formData?.used_cars && (
                  <div className={'flex flex-col gap-2'}>
                    <span className={'font-bold text-xs'}>
                      Used Cars ({dealershipStats?.usedVehicles})
                    </span>
                    <div className={'flex flex-row gap-3'}>
                      {take(
                        inventory?.data?.filter(
                          (vehicle) =>
                            vehicle.image_url.length > 0 &&
                            vehicle.vehicle_state_id !== 1,
                        ),
                        10,
                      )?.map((vehicle) => {
                        return (
                          <Tooltip
                            key={vehicle?.vin_number}
                            label={`${vehicle?.year} ${vehicle?.raw_make} ${vehicle?.raw_model}`}>
                            <div className="w-20 h-20">
                              <img
                                className={'rounded-xl'}
                                src={get(vehicle?.image_url?.match(/https:\/\/.*?(?=\|\|\|\||\|https|,https|;https|$)/), '[0]', '')}
                              />
                            </div>
                          </Tooltip>
                        )
                      })}
                    </div>
                  </div>
                )}
                {formData?.under_30k && (
                  <div className={'flex flex-col gap-2'}>
                    <span className={'font-bold text-xs'}>
                      Under $30k (
                      {Number(
                        inventory?.data?.filter(
                          (vehicle) => Number(vehicle.price) < 30000,
                        ).length,
                      )}
                      )
                    </span>
                    <div className={'flex flex-row gap-3'}>
                      {take(
                        inventory?.data?.filter(
                          (vehicle) =>
                            vehicle.image_url.length > 0 && Number(vehicle.price) < 30000,
                        ),
                        10,
                      )?.map((vehicle) => {
                        return (
                          <Tooltip
                            key={vehicle?.vin_number}
                            label={`${vehicle?.year} ${vehicle?.raw_make} ${
                              vehicle?.raw_model
                            }: $${formatNumber(Number(vehicle?.price))}`}>
                            <div className="w-20 h-20">
                              <img
                                className={'rounded-xl'}
                                src={get(vehicle?.image_url?.match(/https:\/\/.*?(?=\|\|\|\||\|https|,https|;https|$)/), '[0]', '')}
                              />
                            </div>
                          </Tooltip>
                        )
                      })}
                    </div>
                  </div>
                )}
              </div>
              <span className={'text-primary-persian-blue'}>Advanced filters</span>
              <CardTextInput
                label={'Makes'}
                icon={<InventoryIcon />}
                options={[
                  {
                    label: 'Allowed',
                    value: 'allowed_makes',
                  },
                  {
                    label: 'Disallowed',
                    value: 'disallowed_makes',
                  },
                ]}
                name={'make'}
                isCollapsed={formData.make === 'any'}
              />
              <CardTextInput
                label={'Models'}
                icon={<InventoryIcon />}
                options={[
                  {
                    label: 'Allowed',
                    value: 'allowed_models',
                  },
                  {
                    label: 'Disallowed',
                    value: 'disallowed_models',
                  },
                ]}
                name={'model'}
                isCollapsed={formData.model === 'any'}
              />
              <CardTextInput
                label={'Price'}
                icon={<DollarSign />}
                options={[
                  {
                    label: 'Lowest',
                    value: 'lowest_allowed_price',
                  },
                  {
                    label: 'Highest',
                    value: 'highest_allowed_price',
                  },
                ]}
                name={'price'}
                isCollapsed={formData.price === 'any'}
                additionalRadios={[
                  {
                    label: 'Exclude vehicles with no price?',
                    value: 'no_price_only',
                  },
                ]}
              />
              <CardTextInput
                label={'Mileage'}
                icon={<SpeedometerIcon />}
                options={[
                  {
                    label: 'Lowest',
                    value: 'lowest_allowed_mileage',
                  },
                  {
                    label: 'Highest',
                    value: 'highest_allowed_mileage',
                  },
                ]}
                name={'mileage'}
                isCollapsed={formData.mileage === 'any'}
              />
              <CardTextInput
                label={'Stock Number Prefixes'}
                icon={<StockIcon />}
                options={[
                  {
                    label: 'Allowed',
                    value: 'allowed_stock_number_prefixes',
                  },
                  {
                    label: 'Disallowed',
                    value: 'disallowed_stock_number_prefixes',
                  },
                ]}
                name={'stock_number_prefixes'}
                isCollapsed={formData.stock_number_prefixes === 'any'}
              />
              <CardTextInput
                label={'Days on Lot'}
                icon={<CalendarIcon />}
                options={[
                  {
                    label: 'Min',
                    value: 'minimum_age_days',
                  },
                  {
                    label: 'Max',
                    value: 'maximum_age_days',
                  },
                ]}
                name={'days_on_lot'}
                isCollapsed={formData.days_on_lot === 'any'}
              />
            </div>
          )}
        </motion.div>
        <motion.div
          layout
          layoutId={'buttons-container'}
          className={
            'h-20 items-center flex justify-between border-t-2 py-4 px-12 absolute bottom-0 w-full'
          }>
          <Button
            variant={'secondary'}
            onClick={(e) => {
              e.preventDefault()
              navigate('..')
            }}>
            Cancel
          </Button>
          <CampaignProgressBar step={step} />
          <Button variant={'primary'} disabled={isSaving}>
            Continue
          </Button>
        </motion.div>
      </motion.form>
    </FormProvider>
  )
}

export default CreateNewCampaignFilters
